// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  AlertTriangle,
  Plus,
  Trash2,
  Edit3,
  CheckCircle2,
  LayoutGrid,
  Box,
} from "lucide-react";
import toast from "react-hot-toast";
import { availabilityService } from "../../services/AvailabilityService";
import BlockAvailabilityForm from "../../components/admin/BlockAvailabilityForm";
import InlineNotification from "../../components/InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";

const BlockAvailability = () => {
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 25,
    total: 0,
    hasMore: false,
  });

  // Use the common notification system
  const { notification, showSuccess, showError } = useInlineNotification();

  // Show error in toast and scroll to top so user always sees it
  const showErrorAndFocus = (message) => {
    showError(message);
    toast.error(message, { duration: 6000 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const slotTypes = [
    { value: "full_day", label: "Ganztägig" },
    { value: "half_am", label: "Vormittag (AM)" },
    { value: "half_pm", label: "Nachmittag (PM)" },
    { value: "custom", label: "Benutzerdefiniert" },
    { value: "hourly", label: "Stündlich (Delivery)" },
  ];

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const response = await availabilityService.getAvailabilityScenarios();
      setScenarios(response);
    } catch (error) {
      console.error("Error fetching scenarios:", error);
    }
  };

  useEffect(() => {
    fetchAvailabilityData();
  }, [pagination.skip, pagination.limit]);

  const fetchAvailabilityData = async () => {
    try {
      setLoading(true);
      const response = await availabilityService.getAvailability(
        pagination.skip,
        pagination.limit,
      );

      const transformedData = response.items.map((item) => ({
        id: item.uuid,
        uuid: item.uuid,
        scenario_id: item.scenario_id,
        scenario_name: item.scenario_description || item.scenario_name, // Use scenario_description as the display name
        occupied_from: item.occupied_from,
        occupied_to: item.occupied_to,
        slot_type: item.slot_type,
        description:
          item.description || item.scenario_description || "Keine Beschreibung",
        created_at: item.created_at || new Date().toISOString(),
        is_available: item.is_available,
        active_bookings_count: item.active_bookings_count,
      }));

      setBlockedPeriods(transformedData);
      setPagination((prev) => ({
        ...prev,
        total: response.total,
        hasMore: response.has_more,
      }));
    } catch (error) {
      console.error("Error fetching availability data:", error);
      showErrorAndFocus("Fehler beim Laden der Sperrzeiten");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvailability = async (availabilityData, editingId) => {
    try {
      if (editingId) {
        await availabilityService.updateAvailability(
          editingId,
          availabilityData,
        );
        showSuccess("Sperrzeit erfolgreich aktualisiert");
      } else {
        await availabilityService.createAvailability(availabilityData);
        showSuccess("Sperrzeit erfolgreich erstellt");
      }
      fetchAvailabilityData();
      setIsFormVisible(false);
      setEditingPeriod(null);
    } catch (error) {
      console.error("Error saving availability:", error);
      const apiMessage =
        error.response?.data?.detail?.message || error.response?.data?.message;
      showErrorAndFocus(apiMessage || "Fehler beim Speichern der Sperrzeit");
    }
  };

  const handleEdit = (period) => {
    setSelectedItemId(period.uuid);
    setEditingPeriod(period);
    setIsFormVisible(true);
    setIsDeleteMode(false);
  };

  const handleDelete = async (period) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie diese Sperrzeit entfernen möchten?",
      )
    )
      return;

    try {
      const deleteId = period.uuid;
      await availabilityService.deleteAvailability(deleteId);
      setBlockedPeriods((prev) => prev.filter((p) => p.uuid !== period.uuid)); // Use uuid for filtering
      showSuccess("Sperrzeit erfolgreich entfernt");
    } catch (error) {
      console.error("Error deleting availability:", error);
      showErrorAndFocus("Fehler beim Entfernen der Sperrzeit");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPeriods.length === 0) {
      showErrorAndFocus(
        "Bitte wählen Sie mindestens eine Sperrzeit zum Entfernen aus",
      );
      return;
    }

    if (
      !window.confirm(
        `Sind Sie sicher, dass Sie ${selectedPeriods.length} Sperrzeit(en) entfernen möchten?`,
      )
    )
      return;

    try {
      for (const periodId of selectedPeriods) {
        const period = blockedPeriods.find((p) => p.uuid === periodId);
        if (period) {
          const deleteId = period.uuid;
          await availabilityService.deleteAvailability(deleteId);
        }
      }
      setBlockedPeriods((prev) =>
        prev.filter((period) => !selectedPeriods.includes(period.uuid)),
      );
      setSelectedPeriods([]);
      setIsDeleteMode(false);
      showSuccess(
        `${selectedPeriods.length} Sperrzeit(en) erfolgreich entfernt`,
      );
    } catch (error) {
      console.error("Error bulk deleting availability:", error);
      showErrorAndFocus("Fehler beim Entfernen der Sperrzeiten");
    }
  };

  const togglePeriodSelection = (periodId) => {
    setSelectedPeriods((prev) =>
      prev.includes(periodId)
        ? prev.filter((id) => id !== periodId)
        : [...prev, periodId],
    );
  };

  const selectAllPeriods = () => {
    setSelectedPeriods(blockedPeriods.map((period) => period.uuid));
  };

  const clearSelection = () => {
    setSelectedPeriods([]);
    setIsDeleteMode(false);
  };

  const addNewBlockedPeriod = () => {
    setIsFormVisible(true);
    setEditingPeriod(null);
    setIsDeleteMode(false);
    setSelectedItemId(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSlotTypeLabel = (slotType) => {
    return slotTypes.find((type) => type.value === slotType)?.label || slotType;
  };

  // Add this function to handle both success and error notifications from the form
  const handleFormNotification = (message, type = "success") => {
    if (type === "error") {
      showErrorAndFocus(message);
    } else {
      showSuccess(message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-gray-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Sperrzeiten verwalten"
        subtitle="Verwalten Sie gesperrte Zeiträume um Buchungen während Wartungsarbeiten oder besonderen Veranstaltungen zu verhindern"
        buttons={[
          {
            text: "Sperrzeit hinzufügen",
            icon: <Plus className="h-4 w-4" />,
            onClick: addNewBlockedPeriod,
            className:
              "bg-primary-color text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200">
        {/* Common Notification Component */}
        <InlineNotification notification={notification} />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border border-white/20 dark:border-white/10 overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Szenarien
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    3
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 text-purple-600 dark:text-purple-400">
                <Calendar className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border border-white/20 dark:border-white/10 overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Aktiv
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    3
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border border-white/20 dark:border-white/10 overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Flächen
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    28
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 text-indigo-600 dark:text-indigo-400">
                <LayoutGrid className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="relative p-5 rounded-2xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border border-white/20 dark:border-white/10 overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Gesamtfläche
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    4.1K m²
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 text-cyan-600 dark:text-cyan-400">
                <Box className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {isDeleteMode && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-medium text-red-900 dark:text-red-200">
                  Sperrzeiten entfernen
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-700 dark:text-red-300">
                    {selectedPeriods.length} von {blockedPeriods.length}{" "}
                    ausgewählt
                  </span>
                  {selectedPeriods.length < blockedPeriods.length && (
                    <button
                      onClick={selectAllPeriods}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
                    >
                      Alle auswählen
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedPeriods.length === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedPeriods.length === 0
                      ? "bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-slate-400 cursor-not-allowed"
                      : "bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600"
                  }`}
                >
                  <Trash2 className="h-4 w-4" />
                  {selectedPeriods.length} entfernen
                </button>
                <button
                  onClick={clearSelection}
                  className="flex-1 px-4 py-2 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="flex xl:gap-4 gap-4">
          <div
            className={`bg-white dark:bg-slate-800 rounded-2xl shadow-sm border dark:border-slate-700 transition-all duration-300 ${isFormVisible ? "w-1/2" : "w-full"}`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
                    Aktuelle Sperrzeiten
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 mt-1">
                    {isDeleteMode
                      ? `${blockedPeriods.length} Sperrzeit(en) verfügbar - Wählen Sie die zu entfernenden aus`
                      : `${blockedPeriods.length} aktive Sperrzeit(en)`}
                  </p>
                </div>
                {isDeleteMode && (
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    Entfernen-Modus aktiv
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <PageLoader message="Lade Sperrzeiten..." />
            ) : blockedPeriods.length === 0 ? (
              <div className="p-12 text-center sd">
                <Calendar className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">
                  Keine Sperrzeiten vorhanden
                </h3>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Erstellen Sie Ihre erste Sperrzeit um Buchungen während
                  Wartungsarbeiten oder besonderen Veranstaltungen zu
                  verhindern.
                </p>
                <button
                  onClick={addNewBlockedPeriod}
                  className="bg-primary-color text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
                >
                  Sperrzeit hinzufügen
                </button>
              </div>
            ) : (
              <div className={`p-4 pb-0 space-y-3 text-left space-y-4`}>
                <div className="w-full">
                  {blockedPeriods.map((period) => (
                    <div
                      key={period.uuid}
                      onClick={() => handleEdit(period)}
                      className={`border-2 rounded-xl p-4 mb-4 cursor-pointer transition-all duration-200 ${
                        isDeleteMode
                          ? selectedPeriods.includes(period.uuid)
                            ? "bg-red-50 dark:bg-red-900/30 border-l-4 border-red-400 dark:border-red-500 active"
                            : "hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                          : "hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      }
                        ${
                          selectedItemId === period.uuid
                            ? "hover:shadow-md border-primary-color bg-primary-color-tint shadow-lg"
                            : "border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700/50"
                        }
                        `}
                    >
                      <div className="flex items-start justify-between">
                        {isDeleteMode && (
                          <div className="flex items-center mr-4 mt-1">
                            <input
                              type="checkbox"
                              checked={selectedPeriods.includes(period.uuid)}
                              onChange={() =>
                                togglePeriodSelection(period.uuid)
                              }
                              className="h-4 w-4 text-red-600 border-gray-300 dark:border-slate-500 dark:bg-slate-700 rounded focus:ring-red-500"
                            />
                          </div>
                        )}
                        <div
                          className="flex-1"
                          onClick={() =>
                            isDeleteMode && togglePeriodSelection(period.uuid)
                          }
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                              {period.scenario_name}
                            </h3>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                period.slot_type === "full_day"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200"
                                  : period.slot_type === "half_am"
                                    ? "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200"
                                    : period.slot_type === "half_pm"
                                      ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200"
                                      : period.slot_type === "hourly"
                                        ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                                        : "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200"
                              }`}
                            >
                              {getSlotTypeLabel(period.slot_type)}
                            </span>
                            {period.active_bookings_count > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-color-tint primary-color">
                                {period.active_bookings_count} aktive Buchungen
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-slate-400 mb-2">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatDate(period.occupied_from)} →{" "}
                              {formatDate(period.occupied_to)}
                            </div>
                          </div>

                          <p className="text-gray-700 dark:text-slate-300 mb-2">
                            {period.description}
                          </p>

                          <p className="text-xs text-gray-500 dark:text-slate-500">
                            Erstellt: {formatDate(period.created_at)}
                          </p>
                        </div>
                        {!isDeleteMode && (
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleEdit(period)}
                              className="p-2 text-gray-600 dark:text-slate-400 hover:text-primary-color hover:bg-primary-color-tint rounded-lg transition-colors"
                              title="Bearbeiten"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(period)}
                              className="p-2 text-gray-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Löschen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <BlockAvailabilityForm
            isVisible={isFormVisible && !isDeleteMode}
            editingPeriod={editingPeriod}
            scenarios={scenarios}
            onSave={handleSaveAvailability}
            onCancel={() => {
              setIsFormVisible(false);
              setEditingPeriod(null);
              setSelectedItemId(null);
            }}
            onNotification={handleFormNotification}
            mode={editingPeriod ? "edit" : "add"}
          />
        </div>
      </div>
    </div>
  );
};
export default BlockAvailability;
